import { useTranslation } from 'react-i18next'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { CustomerIndexViewModel } from '@ail/customers-api'
import { IUserViewModel } from '../../types/interfaces'
import Modal from '../common/Modal/Modal'

interface CompanySelectorProps {
  visible: boolean
  customers: CustomerIndexViewModel[]
  closeModal: (companiesFromFilter: CustomerIndexViewModel[]) => void
  user?: IUserViewModel | null
  currentSelectedCustomersKeys?: string[]
}

export default function DeliveryCompanySelector(props: CompanySelectorProps) {
  const { visible, closeModal } = props
  const { t } = useTranslation('common')
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]) //Array of customer id
  const [companyFilterSearchString, setCompanyFilterSearchString] = useState<string>('')
  const [selectorCustomersList, setSelectorCustomersList] = useState<CustomerIndexViewModel[]>([]) //List of customers for selector display
  const [showList, setShowList] = useState<boolean>(false)

  useEffect(() => {
    if (props.currentSelectedCustomersKeys) {
      setSelectedCustomers(props.currentSelectedCustomersKeys)
    }
  }, [props.currentSelectedCustomersKeys])

  const companiesFromFilter = useMemo(() => {
    if (selectedCustomers && selectedCustomers.length > 0 && props.customers && props.customers.length > 0) {
      return props.customers.filter((c) => c.value && selectedCustomers.includes(c.value))
    }
    return []
  }, [selectedCustomers, props.customers])

  const filterCustomers = useCallback(
    (searchTerm: string | null) => {
      let filteredCustomers = props.customers

      const searchQuery = searchTerm?.trim().toLowerCase() || ''
      if (searchQuery) {
        filteredCustomers = filteredCustomers.filter((customer) =>
          customer.customer?.name?.toLowerCase().includes(searchQuery)
        )
      }

      if (selectedCustomers.length > 0) {
        filteredCustomers = filteredCustomers.filter(
          (customer) => customer.value && !selectedCustomers.includes(customer.value)
        )
      }

      setSelectorCustomersList(filteredCustomers)
    },
    [props.customers, selectedCustomers]
  )

  useEffect(() => {
    filterCustomers(companyFilterSearchString)
  }, [companyFilterSearchString, filterCustomers])

  function selectCustomer(customerKey: string, remove?: boolean) {
    const companies = [...selectedCustomers]
    if (remove) {
      const idx = companies.indexOf(customerKey)
      if (idx > -1) {
        companies.splice(idx, 1)
      }
    } else {
      companies.push(customerKey)
    }
    setSelectedCustomers(companies)
    if (!remove) {
      setSelectorCustomersList(selectorCustomersList.filter((c) => c.value !== customerKey))
    }
  }

  function selectAllCustomers() {
    const companies = [...selectedCustomers]
    selectorCustomersList.forEach((el) => {
      if (el.value) companies.push(el.value)
    })
    setSelectedCustomers(companies)

    filterCustomers(null)
    setShowList(false)
  }

  function closeModalAndUpdateParent() {
    setShowList(false)
    setCompanyFilterSearchString('')
    closeModal(companiesFromFilter)
  }

  function listDisplayToggle(showList: boolean) {
    if (showList && !!companyFilterSearchString) {
      setShowList(true)
    }
    if (!showList) {
      setShowList(false)
    }
    if (showList && !companyFilterSearchString) {
      setShowList(true)
    }
  }

  function onStringEntry(str: string) {
    setCompanyFilterSearchString(str)
    setShowList(true)
  }
  return (
    <Modal
      title={t('CUSTOMER_SEARCH')}
      visible={visible}
      onClose={() => {
        closeModalAndUpdateParent()
      }}
    >
      <div className="customer-name-filter-ui">
        {companiesFromFilter.length > 0 && (
          <div className="active-items">
            {companiesFromFilter.map((c, idx) => (
              <span key={idx}>
                <span>{c.customer?.name} </span>
                <i className="icon-close" onClick={() => c.value && selectCustomer(c.value, true)} />
              </span>
            ))}
          </div>
        )}
        {companiesFromFilter.length > 0 && (
          <div className="activeFilter">
            <span>
              {companiesFromFilter.length} {t('SELECTED')}
            </span>
            <a onClick={() => setSelectedCustomers([])}>
              <i className="icon-clear" /> {t('CLEAR_SELECTED')}
            </a>
          </div>
        )}
        <div className="search">
          <div className="search-selector">
            <input
              autoFocus={true}
              type="text"
              value={companyFilterSearchString}
              name={`fieldnameveryunique${new Date().getTime()}`}
              autoComplete="none"
              list="autocompleteOff"
              placeholder={t('CUSTOMER_KEYWORDS')}
              onChange={(e) => onStringEntry(e.target.value)}
              onClick={() => listDisplayToggle(true)}
            />
            <i className={`icon-${showList ? 'up' : 'bottom'}`} onClick={() => listDisplayToggle(!showList)} />
            <i className="icon-search" />

            {showList && (
              <div className="search-results">
                <div className="resultsArea">
                  <ul>
                    {selectorCustomersList.map(
                      (c, idx) =>
                        idx <= 50 && (
                          <>
                            <li key={c.value} className="resultItem">
                              <div className="result" onClick={() => c.value && selectCustomer(c.value)}>
                                <span className="name">{c.customer?.name}</span>
                                {c.customer?.reference && <span className="ref">Miles ID: {c.customer.reference}</span>}
                                {c.customer?.vatNumber && <span className="vat">{c.customer.vatNumber}</span>}
                                <span className="addr">
                                  {`${c.customer?.primary?.addresses?.[0]?.postalCode || ''} ${
                                    c.customer?.primary?.addresses?.[0]?.city || ''
                                  } ${c.customer?.primary?.addresses?.[0]?.street || ''} ${
                                    c.customer?.primary?.addresses?.[0]?.country || ''
                                  }`.replace('  ', ' ')}
                                </span>
                              </div>
                            </li>
                          </>
                        )
                    )}
                    {selectorCustomersList?.length > 50 && (
                      <li style={{ backgroundColor: 'gray' }}>{t('MORE_COMPANIES_AVAILABLE')}</li>
                    )}
                  </ul>
                  <div className="searchFilters">
                    {selectorCustomersList.length} {t('RESULTS')}
                    {!props.user?.isInternalUser && selectorCustomersList.length > 0 && (
                      <a title="Select All" onClick={() => selectAllCustomers()}>
                        <i className="icon-selectAll" />
                        Select All
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="btnArea">
            {companiesFromFilter.length >= 5 && (
              <div className="warningArea">
                <i className="icon-warning" />
                <span>{t('GREAT_COMPANIES_AMOUNT_CAN_AFFECT_PERFORMANCE')}</span>
              </div>
            )}
            {companiesFromFilter.length > 0 && (
              <div>
                <button title={t('APPLY')} onClick={() => closeModalAndUpdateParent()}>
                  {t('APPLY')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}
